// eslint-disable-next-line no-unused-vars
import {useEffect, useRef} from "react";
import {useHistory} from "react-router-dom";
import {App} from "@capacitor/app";
import {Capacitor} from "@capacitor/core";
import {ANDROID_TABS, PAGE_URLS as PAGES_URLS} from "constants/AppConstants";
import {registerPlugin} from "@capacitor/core";
import {handleAndroidBackPress} from "utils/androidBackNavigation";
import {getAndroidTabForRoute} from "utils/androidTabSync";
import type {PluginListenerHandle} from "@capacitor/core";

interface TabBarPluginInterface {
  changeTab: (options: {tab: string}) => Promise<void>;
  addListener: (eventName: string, listenerFunc: (...args: any[]) => any) => Promise<PluginListenerHandle>;
}

const TabBarPlugin = registerPlugin<TabBarPluginInterface>("TabBarPlugin");

const AppUrlListener = () => {
  const history = useHistory();
  const isAndroid = Capacitor.getPlatform() === "android";
  const lastSyncedTabRef = useRef(null);

  useEffect(() => {
    let navigateListener;
    let backListener;
    let appUrlOpenListener;
    let unlistenHistory;

    // Listen to navigation commands from native Android
    if (isAndroid) {
      const syncNativeTabToRoute = async (nextLocation) => {
        const nextTab = getAndroidTabForRoute(nextLocation);
        if (!nextTab || nextTab === lastSyncedTabRef.current) {
          return;
        }

        // Routes native itself pushed (via the "navigate" listener below, tagged with
        // mapFromDiary/mapFromExplore) already know which tab they're on — echoing a changeTab
        // call back here is redundant, and worse, races the fallback WebView's visibility back
        // off: native's showTab() resets its "show this WebView page instead of the native
        // screen" override every time DIARY is (re)selected, including for this redundant echo.
        // This only ever bites on the *first* Diary-tab-resolving navigation of a session,
        // because lastSyncedTabRef starts null and the WebView's cold-boot route never resolves
        // to a tracked tab, so there's nothing to compare against until then.
        if (nextLocation?.state?.mapFromDiary || nextLocation?.state?.mapFromExplore) {
          lastSyncedTabRef.current = nextTab;
          return;
        }

        try {
          await TabBarPlugin.changeTab({tab: nextTab});
          lastSyncedTabRef.current = nextTab;
        } catch (_) {
          // keep navigation responsive even if native bridge is temporarily unavailable
        }
      };

      (async () => {
        navigateListener = await TabBarPlugin.addListener("navigate", (data) => {
          if (data.path) {
            // Native is telling us to navigate to a specific path
            if (data.state) {
              history.push(data.path, data.state);
            } else {
              history.push(data.path);
            }
          }
        });

        backListener = await TabBarPlugin.addListener("backPressed", () => {
          handleAndroidBackPress(history);
        });

        unlistenHistory = history.listen((locationUpdate) => {
          void syncNativeTabToRoute(locationUpdate);
        });

        void syncNativeTabToRoute(history.location);
      })();
    }

    (async () => {
      appUrlOpenListener = await App.addListener("appUrlOpen", async (event) => {
        // Extract the path from the URL by stripping the origin portion.
        // This supports multiple domains (e.g. waterspeedapp.web.app, go.waterspeedapp.com)
        // without relying on a hardcoded split on ".app".
        const extractPath = (url) => {
          try {
            const parsed = new URL(url);
            return parsed.pathname + parsed.search + parsed.hash;
          } catch {
            // Fallback: strip everything up to and including the first "/" after the host
            const match = url.match(/^https?:\/\/[^/]+(.*)/);
            return match ? match[1] : url;
          }
        };

        const extractPathNoParams = (url) => {
          try {
            const parsed = new URL(url);
            return parsed.pathname;
          } catch {
            return extractPath(url).split("?")[0];
          }
        };

        let slug = extractPath(event.url);
        let slugWithoutParams = extractPathNoParams(event.url);

        // Handle OAuth redirects (Garmin, GPS Speedsurfing, Suunto, Coros)
        // These still need to be handled in the web layer
        if (
          slugWithoutParams === extractPath(process.env.REACT_APP_ANDROID_GARMIN_REDIRECT_URI).split("?")[0] ||
          slugWithoutParams ===
            extractPath(process.env.REACT_APP_ANDROID_GPS_SPEEDSURFING_REDIRECT_URI).split("?")[0] ||
          slugWithoutParams === extractPath(process.env.REACT_APP_ANDROID_SUUNTO_REDIRECT_URI).split("?")[0] ||
          slugWithoutParams === extractPath(process.env.REACT_APP_ANDROID_COROS_REDIRECT_URI).split("?")[0]
        ) {
          await TabBarPlugin.changeTab({tab: ANDROID_TABS.ACCOUNT});
          history.push(slug);
          return;
        }

        // Handle /redirect-app links (legacy support)
        if (slugWithoutParams.startsWith(PAGES_URLS.publicRedirectApp)) {
          slugWithoutParams = slugWithoutParams.replace(PAGES_URLS.publicRedirectApp, "");
          slug = slug.replace(PAGES_URLS.publicRedirectApp, "");
          const firstPath = `/${slugWithoutParams.split("/")[1]}`;

          if (PAGES_URLS.challengesPageUrl.startsWith(firstPath)) {
            const pathParts = slug.split("/").slice(1);
            await TabBarPlugin.changeTab({tab: ANDROID_TABS.CHALLENGES});

            if (pathParts.length >= 2) {
              history.push(PAGES_URLS.challengesPageUrl, {challenge: pathParts[1]});
            } else {
              history.push(PAGES_URLS.challengesPageUrl);
            }
          } else if (
            PAGES_URLS.diaryPageUrl.startsWith(firstPath) ||
            PAGES_URLS.oldDiaryPageUrl.startsWith(firstPath)
          ) {
            await TabBarPlugin.changeTab({tab: ANDROID_TABS.DIARY});
            history.push(PAGES_URLS.diaryPageUrl);
          } else if (PAGES_URLS.statisticsPageUrl.startsWith(firstPath)) {
            await TabBarPlugin.changeTab({tab: ANDROID_TABS.DIARY});
            history.push(PAGES_URLS.diaryPageUrl);
            setTimeout(() => {
              history.push(PAGES_URLS.statisticsPageUrl);
            });
          } else if (PAGES_URLS.recordPageUrl.startsWith(firstPath)) {
            await TabBarPlugin.changeTab({tab: ANDROID_TABS.RECORD});
          } else if (PAGES_URLS.settingsPageUrl.startsWith(firstPath)) {
            await TabBarPlugin.changeTab({tab: ANDROID_TABS.ACCOUNT});
            history.push(PAGES_URLS.androidSettingsPageUrl);
          } else {
            await TabBarPlugin.changeTab({tab: ANDROID_TABS.RECORD});
          }
          return;
        }

        // On Android, all direct navigation links are handled natively
        // We don't need to do anything here - native will send us a "navigate" event
      });
    })();

    return () => {
      navigateListener?.remove();
      backListener?.remove();
      appUrlOpenListener?.remove();
      unlistenHistory?.();
    };
  }, [history, isAndroid]);

  return null;
};

export default AppUrlListener;
